import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import Cookies from 'js-cookie'
import { useDomainStore } from '@/src/types/DomainStore'

interface ApiOptions {
  path: string
  method: 'get' | 'post' | 'patch' | 'delete'
  token?: string
  serverParams?: { [key: string]: string } | any | null
  data?: any
  headers?: { [key: string]: string } | any | null
  signal?: AbortSignal
  // For endpoints that answer with a file rather than JSON. When set, the
  // whole axios response is handed back instead of `response.data`, because a
  // download also needs the filename off the Content-Disposition header.
  responseType?: 'blob'
}

const CommonApi = async ({
  path,
  method,
  data,
  headers,
  serverParams,
  signal,
  responseType
}: ApiOptions) => {
  const baseURL = process.env.NEXT_PUBLIC_BASE_URL
  const accessToken = Cookies.get('access_token')
  const userId = Cookies.get('user_id')
  const url = `${baseURL}${path}`

  // Build headers deliberately to avoid unnecessary pre-flights
  const reqHeaders: Record<string, string> = {}

  if (baseURL?.includes('ngrok')) {
    reqHeaders['ngrok-skip-browser-warning'] = 'true'
  }

  // Merge header maps from `headers`
  if (headers && typeof headers === 'object') {
    Object.assign(reqHeaders, headers as Record<string, string>)
  }

  if (accessToken) {
    reqHeaders.Authorization = `Bearer ${accessToken}`
    if (userId) reqHeaders.user = userId
    // Only attach portalAccess when authenticated (used by BE to gate access)
    const portal = sessionStorage.getItem('portalVerification') || 'false'
    reqHeaders.portalAccess = portal
  }

  // Pass through serverParams when explicitly provided (SSR needs this)
  if (serverParams !== undefined && serverParams !== null) {
    reqHeaders.serverParams = String(serverParams)
  }

  const config: AxiosRequestConfig = {
    method,
    url,
    data,
    signal,
    headers: reqHeaders,
    ...(responseType ? { responseType } : {})
  }

  try {
    const response = await axios(config)
    if (responseType) {
      return response
    }
    if (response.status) {
      return response.data
    }
  } catch (error: any) {
    // Deactivated/inactive domain: every endpoint answers with this body (the
    // HTTP status varies between 500 and 503). Flag it once so the app can swap
    // itself for a single message page, and hand the error body back instead of
    // rethrowing — otherwise every caller rejects and crashes its own screen.
    // Returning errData (not undefined) keeps the `if (resp.status)` contract
    // that call sites rely on, since it already carries status:false + msg.
    const errData = error.response?.data
    const errMsg = typeof errData?.msg === 'string' ? errData.msg : ''
    if (errData?.status === false && /inactive|deactivat/i.test(errMsg)) {
      useDomainStore.getState().setDomainInactive(errMsg)
      return errData
    }

    // Logout and redirect to login if getting a 403 (invalid access)
    if (error.response?.status === 403) {
      Cookies.remove('access_token')
      Cookies.remove('userId')
      window.location.href = '/login?redirect_url=__widget_login__'
    }

    // Skip error reporting if cancelling or aborting requests
    const isAxiosCancel = axios.isCancel(error) || (error as AxiosError).code === 'ERR_CANCELED'
    const isAbortError = error instanceof DOMException && error.name === 'AbortError'
    if (isAxiosCancel || isAbortError) {
      return Promise.reject(error)
    }

    throw error
  }
}

export default CommonApi
